import React, { useEffect } from "react";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input, Popconfirm } from "antd";
import TextArea from "antd/es/input/TextArea";

import ButtonWrapper from "../../../components/reuseComp/ButtonWrapper";
import UploadImage from "../../../components/UploadImage";

interface ContentItem {
  title: string;
  desc: string;
}

interface ExpertiseProps {
  heading?: string;
  subtext?: string;
  rightImage?: string;
  leftImage?: string[];
  content?: ContentItem[];
}

interface Props {
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  onDelete: (index: number, field: "Our Expertise") => void;
  form: any;
  expertiseData?: ExpertiseProps;
  loading?: boolean;
  mode: "list" | "add" | "edit";
  setMode: (value: "list" | "add" | "edit") => void;
  editingIndex: number | null;
  setEditingIndex: (value: number | null) => void;
}

const OurExpertise: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  expertiseData,
  onDelete,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
}) => {
  useEffect(() => {
    if (!expertiseData) return;

    form.resetFields(["expertise", "content"]);

    if (mode === "list") {
      form.setFieldsValue({
        expertise: {
          heading: expertiseData.heading || "",
          subtext: expertiseData.subtext || "",
          rightImage: expertiseData.rightImage || "",
          leftImage1: expertiseData.leftImage?.[0] || "",
          leftImage2: expertiseData.leftImage?.[1] || "",
          content: expertiseData.content || [],
        },
      });
    }

    if (mode === "add") {
      form.setFieldsValue({
        expertise: {
          content: [{ title: "", desc: "" }],
        },
      });
    }

    if (mode === "edit" && editingIndex !== null) {
      const item = expertiseData.content?.[editingIndex];
      if (item) {
        form.setFieldsValue({
          expertise: {
            content: [{ title: item.title, desc: item.desc }],
          },
        });
      }
    }
  }, [mode, editingIndex, expertiseData]);

  return (
    <div>
      {isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl mb-4">
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Heading"
              name={["expertise", "heading"]}
              rules={[{ required: true, message: "Enter heading" }]}
            >
              <Input placeholder="enter heading..."/>
            </Form.Item>

            <Form.Item
              label="Subtext"
              name={["expertise", "subtext"]}
              rules={[{ required: true, message: "Enter subtext" }]}
            >
              <TextArea rows={3} placeholder="enter subtext..."/>
            </Form.Item>

            <div className="flex justify-between items-center">
              <div>
                <h2 className="mb-2">
                  <span className="text-red-500">*</span> Left Image
                </h2>
                <div className="flex items-center gap-3">
                  <Form.Item
                    name={["expertise", "leftImage1"]}
                    rules={[{ required: true, message: "upload image" }]}
                  >
                    <UploadImage listType="picture-card" />
                  </Form.Item>
                  <Form.Item
                    name={["expertise", "leftImage2"]}
                    rules={[{ required: true, message: "upload image" }]}
                  >
                    <UploadImage listType="picture-card" />
                  </Form.Item>
                </div>
              </div>

              <Form.Item
                label="Right Image"
                name={["expertise", "rightImage"]}
                rules={[{ required: true, message: "upload image" }]}
              >
                <UploadImage listType="picture-card" />
              </Form.Item>
            </div>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                setIsEditing(false);
                handleCancel();
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="rounded-[20px] overflow-hidden">
          <div className="hidden sm:block">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Images</th>
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Subtext</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>

              <tbody className="bg-[#F9F9F9]">
                {expertiseData?.heading || expertiseData?.subtext ? (
                  <tr className="border-b">
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-3">
                        <Image src={expertiseData?.leftImage?.[0]} width={40} />
                        <Image src={expertiseData?.leftImage?.[1]} width={40} />
                        <Image src={expertiseData?.rightImage} width={40} />
                      </div>
                    </td>

                    <td className="px-4 py-3">{expertiseData?.heading}</td>
                    <td className="px-4 py-3">{expertiseData?.subtext}</td>

                    <td className="px-4 py-3 text-right">
                      <EditOutlined
                        className="text-primary text-xl cursor-pointer"
                        onClick={() => setIsEditing(true)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}

                <tr>
                  <td colSpan={4} className="px-4 py-3 text-right">
                    <Button
                      type="dashed"
                      className="text-primary font-semibold"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setMode("add");
                        setEditingIndex(null);
                      }}
                    >
                      Add Sections
                    </Button>
                  </td>
                </tr>

                <tr>
                  <td colSpan={4} className="p-4">
                    <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left">Title</th>
                          <th className="px-4 py-2 text-left">Description</th>
                          <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {expertiseData?.content?.length ? (
                          expertiseData.content.map((item, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-4 py-2">{item.title}</td>
                              <td className="px-4 py-2">{item.desc}</td>
                              <td className="px-4 py-2 text-right">
                                <div className="flex justify-end gap-4 text-lg">
                                  <EditOutlined
                                    className="cursor-pointer text-primary"
                                    onClick={() => {
                                      setMode("edit");
                                      setEditingIndex(index);
                                    }}
                                  />

                                  <Popconfirm
                                    title="Are you sure to delete this?"
                                    onConfirm={() =>
                                      onDelete(index, "Our Expertise")
                                    }
                                  >
                                    <DeleteOutlined className="cursor-pointer text-red-500" />
                                  </Popconfirm>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={3}
                              className="px-4 py-6 text-center text-gray-500"
                            >
                              No sections added
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-4">
            <div className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm">
              <div className="flex gap-3 mb-3">
                <Image src={expertiseData?.leftImage?.[0]} width={50} />
                <Image src={expertiseData?.leftImage?.[1]} width={50} />
                <Image src={expertiseData?.rightImage} width={50} />
              </div>

              <div className="space-y-2">
                <div>
              <div><b className="text-primary">Heading:</b></div>
                  <p>{expertiseData?.heading || "—"}</p>
                </div>

                <div>
              <div><b className="text-primary">Subtext:</b></div>
                  <p>{expertiseData?.subtext || "—"}</p>
                </div>
              </div>

              <div className="flex justify-end text-primary text-[20px] mt-2">
                <EditOutlined
                  className="cursor-pointer text-lg"
                  onClick={() => setIsEditing(true)}
                />
              </div>
            </div>

            <div className="text-right">
              <Button
                type="dashed"
                className="text-primary font-semibold"
                icon={<PlusOutlined />}
                onClick={() => {
                  setMode("add");
                  setEditingIndex(null);
                }}
              >
                Add Sections
              </Button>
            </div>

            {expertiseData?.content?.length ? (
              expertiseData.content.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm"
                >
                  <div className="space-y-2">
                    <div>
                      <span className="font-semibold text-primary">Title:</span>
                      <p className="text-sm">{item?.title}</p>
                    </div>

                    <div>
                      <span className="font-semibold text-primary">Description:</span>
                      <p className="text-sm">{item?.desc}</p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-4 text-[20px] mt-2 text-primary">
                    <EditOutlined
                      className="cursor-pointer"
                      onClick={() => {
                        setMode("edit");
                        setEditingIndex(index);
                      }}
                    />

                    <Popconfirm
                      title="Are you sure to delete this?"
                      onConfirm={() => onDelete(index, "Our Expertise")}
                    >
                      <DeleteOutlined className="cursor-pointer text-red-500 text-[20px]" />
                    </Popconfirm>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-6">
                No sections added
              </div>
            )}
          </div>
        </div>
      )}

      {mode !== "list" && !isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl">
          <Form layout="vertical" form={form}>
            <Form.List name={["expertise", "content"]}>
              {(fields) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key} className="border p-4 rounded mb-4">
                      <Form.Item
                        label="Title"
                        name={[field.name, "title"]}
                        rules={[{ required: true, message: "Enter title" }]}
                      >
                        <Input placeholder="enter title..."/>
                      </Form.Item>

                      <Form.Item
                        label="Description"
                        name={[field.name, "desc"]}
                        rules={[
                          { required: true, message: "Enter description" },
                        ]}
                      >
                        <TextArea rows={2} placeholder="enter decription..."/>
                      </Form.Item>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                handleCancel();
                setMode("list");
                setEditingIndex(null);
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default OurExpertise;
